import { useCallback, useRef } from 'react';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import {
    conversationDedupeKey,
    deduplicateConversations,
} from '../../../services/repositories/helpers/ChatConversationMapper';
import { ChatConversation } from './types';

interface UseConversationRefreshOptions {
    organizationId?: string;
    connectionId?: string;
    enabled?: boolean;
    pageSize: number;
    isLoadingRef: React.MutableRefObject<boolean>;
    hasLoadedOnceRef: React.MutableRefObject<boolean>;
    onConversations: React.Dispatch<React.SetStateAction<ChatConversation[]>>;
    onInitialLoad: () => Promise<void>;
}

/**
 * Merge inteligente: atualiza/adiciona novas conversas, mantém antigas fora do range.
 * Chave = connectionId + contato — evita WA A sobrescrever WA B / IG no mesmo telefone.
 */
export function mergeConversations(
    prev: ChatConversation[],
    incoming: ChatConversation[]
): ChatConversation[] {
    const newMap = new Map(incoming.map((c) => [conversationDedupeKey(c), c]));

    const merged = prev.map((existingConv) => {
        const key = conversationDedupeKey(existingConv);
        const updated = newMap.get(key);
        if (!updated) return existingConv;
        newMap.delete(key);
        return updated;
    });

    const newConvs = Array.from(newMap.values());
    return deduplicateConversations([...newConvs, ...merged]);
}

/** Snapshot leve para o polling saber se houve mudança real (backoff). */
export function conversationsChanged(
    prev: ChatConversation[],
    next: ChatConversation[],
): boolean {
    if (prev.length !== next.length) return true;
    for (let i = 0; i < next.length; i += 1) {
        const a = prev[i];
        const b = next[i];
        if (
            a.id !== b.id
            || a.connectionId !== b.connectionId
            || a.unreadCount !== b.unreadCount
            || a.lastMessage !== b.lastMessage
            || a.assignedTo !== b.assignedTo
            || a.status !== b.status
            || a.avatar !== b.avatar
            || (a.lastMessageTime?.getTime() ?? 0) !== (b.lastMessageTime?.getTime() ?? 0)
        ) {
            return true;
        }
    }
    return false;
}

export function useConversationRefresh({
    organizationId,
    connectionId,
    enabled,
    pageSize,
    isLoadingRef,
    hasLoadedOnceRef,
    onConversations,
    onInitialLoad,
}: UseConversationRefreshOptions) {
    const pendingRefreshRef = useRef(false);

    const refresh = useCallback(async (): Promise<boolean> => {
        if (!enabled || !organizationId) return false;

        // Se já carregando, agenda um único refresh — evita cadeia sob tempestade PartyKit
        if (isLoadingRef.current) {
            pendingRefreshRef.current = true;
            return true;
        }

        // Se nunca carregou, delega ao loadInitial
        if (!hasLoadedOnceRef.current) {
            await onInitialLoad();
            return true;
        }

        isLoadingRef.current = true;
        let changed = false;

        try {
            const result = await chatRepository.getConversationsPaginated(
                organizationId,
                undefined,
                pageSize,
                connectionId
            );

            // Merge sem flash visual (isLoading não é alterado no refresh)
            onConversations((prev) => {
                const next = mergeConversations(prev, result.conversations);
                changed = conversationsChanged(prev, next);
                return next;
            });
        } catch (error) {
            console.error('[useConversationRefresh] Erro ao atualizar conversas:', error);
            changed = true;
        } finally {
            isLoadingRef.current = false;
        }

        // Um único pending — sem reencadear a cada overlap sob carga pós-connect
        if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            setTimeout(() => { void refresh(); }, 100);
            return true;
        }

        return changed;
    }, [organizationId, connectionId, enabled, pageSize, isLoadingRef, hasLoadedOnceRef, onConversations, onInitialLoad]);

    return { refresh };
}
